import type React from 'react';

import { Separator, Text } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { ModuleSeparator } from '#src/app/pages/starterDashboardV2/shared/components';
import { useRecommendations as useRecommendationsContext } from '#src/app/pages/starterDashboardV2/shared/contexts';
import type { WaypointCallbackArgs } from '#src/app/shared/components/layout/lazy';
import { Waypoint } from '#src/app/shared/components/scroll';

import { EmptyState } from './components/EmptyState';
import { RecommendationsCarousel } from './components/RecommendationsCarousel';
import { ShareUrl } from './components/ShareUrl';
import { useRecommendations } from './hooks/useRecommendations';

export type Props = {
	viewContext: 'share' | 'home';
};

export const Recommendations: React.FC<Props> = ({ viewContext }) => {
	const {
		data: {
			shareRecommendationsLoading,
			shouldDisplayShareRecommendations,
			shareRecommendations,
			isShareRecommendationsEnabled,
		},
	} = useRecommendationsContext();

	const { actions } = useRecommendations();
	const { translateHtml } = useI18n();

	const firstRedditRec = shareRecommendations.reddit.at(0);
	const firstFacebookRec = shareRecommendations.facebook.at(0);

	if (!isShareRecommendationsEnabled) {
		return null;
	}

	if (!shareRecommendationsLoading && !shouldDisplayShareRecommendations) {
		return (
			<>
				<EmptyState />
				<ModuleSeparator />
			</>
		);
	}

	return (
		<>
			<section
				id="recommendations-section"
				aria-labelledby="recommendations-title"
				className="bg-background-base scroll-mt-20 px-4 py-8 pr-0 md:scroll-mt-18 md:p-12 md:pr-0"
			>
				<Waypoint
					onPositionChange={(waypointCallbackArgs: WaypointCallbackArgs) => {
						actions.trackView(
							waypointCallbackArgs,
							firstFacebookRec?.metadata.source,
							firstRedditRec?.metadata.source,
							viewContext,
						);
					}}
				/>
				<div className="flex flex-col gap-6 pr-4 md:pr-12">
					<div className="flex flex-col gap-5">
						<div className="flex flex-col gap-3">
							<Text as="h3" id="recommendations-title" className="text-size-heading-small">
								<Translate value="corgi.petition.starter_dashboard.recommendations.title_variant_two" />
							</Text>
							<Text variant="body">
								{translateHtml('corgi.petition.starter_dashboard.recommendations.description_variant')}&nbsp;
							</Text>
						</div>
						<div className="w-full md:w-auto">
							<ShareUrl viewContext={viewContext} />
						</div>
					</div>
					<div>
						<Separator className="m-0" />
					</div>
					<RecommendationsCarousel
						loading={shareRecommendationsLoading}
						shareRecommendations={shareRecommendations}
						isReskin
						viewContext={viewContext}
					/>
				</div>
			</section>
			<ModuleSeparator />
		</>
	);
};
